import React, { useState } from 'react';
import { useApp } from '../App';
import { fsSet, fsLogChange } from '../lib/firebase';
import { DEFAULT_ACTIVITY_TYPES } from '../config/appConfig';

// ─── ACTIVITY TYPE SETTINGS ─────────────────────────────────────────────────────
// The "What" list a caregiver booking's activity picker draws from (Jiu
// Jitsu, Swim, Dogs Only, ...). Same disabled/custom-additions shape and UI
// pattern as KeywordRegistries (SettingsModule.jsx) — a single flat list
// rather than KeywordRegistries' multiple named registries, since there's
// only one list here.

export default function ActivityTypeSettings() {
  const { familyData, setFamilyData, currentUser } = useApp() || {};
  const saved = familyData?.config?.activityTypes || {};

  const [disabled, setDisabled] = useState(new Set(saved.disabled || []));
  const [custom, setCustom]     = useState(saved.custom || []);
  const [adding, setAdding]     = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving]     = useState(false);
  const [savedOk, setSavedOk]   = useState(false);

  function toggleDefault(id) {
    setDisabled(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setSavedOk(false);
  }

  function addCustom() {
    if (!newLabel.trim()) return;
    setCustom(prev => [...prev, newLabel.trim()]);
    setNewLabel('');
    setAdding(false);
    setSavedOk(false);
  }

  function removeCustom(label) {
    setCustom(prev => prev.filter(l => l !== label));
    setSavedOk(false);
  }

  async function save() {
    if (!familyData?.id) return;
    setSaving(true);
    const payload = { disabled: [...disabled], custom };
    await fsSet(`families/${familyData.id}/config/appConfig`, { activityTypes: payload });
    await fsLogChange(familyData.id, {
      actorId: currentUser?.email, actorName: currentUser?.name,
      module: 'settings', action: 'save_activity_types',
    });
    setFamilyData(prev => ({ ...prev, config: { ...prev.config, activityTypes: payload } }));
    setSaving(false);
    setSavedOk(true);
  }

  return (
    <div className="registry-section">
      <h3 className="registry-section__title">Activity Types</h3>
      <p className="registry-section__note">
        The "What" options offered when booking a caregiver. Built-in — toggle off to disable.
      </p>
      <div className="registry-keywords">
        {DEFAULT_ACTIVITY_TYPES.map(a => {
          const on = !disabled.has(a.id);
          return (
            <button key={a.id} type="button"
              className={`kw-chip ${on ? 'kw-chip--on' : 'kw-chip--off'}`}
              onClick={() => toggleDefault(a.id)}
              title={on ? 'Click to disable' : 'Click to enable'}>
              {a.label}{on ? '' : ' ✕'}
            </button>
          );
        })}
      </div>

      {custom.length > 0 && (
        <>
          <p className="registry-section__note" style={{ marginTop: 8 }}>Custom</p>
          <div className="registry-keywords">
            {custom.map(label => (
              <span key={label} className="kw-chip kw-chip--custom">
                {label}
                <button className="kw-chip__remove" onClick={() => removeCustom(label)}>✕</button>
              </span>
            ))}
          </div>
        </>
      )}

      {adding ? (
        <div className="registry-add-row">
          <input className="input input--sm" placeholder="New activity" value={newLabel} autoFocus
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCustom()} />
          <button className="btn btn--primary btn--small" onClick={addCustom} disabled={!newLabel.trim()}>Add</button>
          <button className="btn btn--ghost btn--small" onClick={() => { setAdding(false); setNewLabel(''); }}>Cancel</button>
        </div>
      ) : (
        <button className="btn btn--ghost btn--small" style={{ marginTop: 8 }}
          onClick={() => { setAdding(true); setNewLabel(''); }}>+ Add activity</button>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
        <button className="btn btn--primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {savedOk && <span style={{ fontSize: 13, color: 'var(--green)' }}>✓ Saved</span>}
      </div>
    </div>
  );
}
